import React, {useState, useCallback, ReactNode} from 'react';
import {Modal, ModalHeader, ModalBody, ModalFooter, Button} from 'reactstrap';

interface ConfirmModalProps {
    isOpen: boolean;
    title: ReactNode;
    content: ReactNode;
    onConfirm: () => void;
    onCancel: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
const ConfirmModal: React.FC<ConfirmModalProps> = ({
    isOpen,
    title,
    content,
    onConfirm,
    onCancel
}) => {
    return (
        <Modal isOpen={isOpen} toggle={onCancel}>
            <ModalHeader toggle={onCancel}>
                <span>{title}</span>
            </ModalHeader>
            <ModalBody>{content}</ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={onCancel}>
                    Cancel
                </Button>
                <Button color="primary" onClick={onConfirm}>
                    Confirm
                </Button>
            </ModalFooter>
        </Modal>
    );
};

interface ConfirmOptions {
    title?: ReactNode;
}

const useConfirm = () => {
    const [modalState, setModalState] = useState<{
        isOpen: boolean;
        title: ReactNode;
        content: ReactNode;
        onConfirm: () => void;
        onCancel: () => void;
    }>({
        isOpen: false,
        title: '',
        content: '',
        onConfirm: () => {},
        onCancel: () => {}
    });

    const closeConfirm = useCallback(() => {
        setModalState(prev => ({...prev, isOpen: false}));
    }, []);

    const confirm = useCallback(
        (
            content: ReactNode,
            onConfirm: () => void,
            options: ConfirmOptions = {},
            onCancel?: () => void
        ) => {
            setModalState({
                isOpen: true,
                title: options.title || 'Confirm',
                content,
                onConfirm: () => {
                    closeConfirm();
                    onConfirm();
                },
                onCancel: () => {
                    closeConfirm();
                    if (onCancel) {
                        onCancel();
                    }
                }
            });
        },
        [closeConfirm]
    );

    const confirmModal = (
        <ConfirmModal
            isOpen={modalState.isOpen}
            title={modalState.title}
            content={modalState.content}
            onConfirm={modalState.onConfirm}
            onCancel={modalState.onCancel}
        />
    );

    return {confirm, confirmModal, closeConfirm};
};

export default useConfirm;
